import React, { useId, useMemo } from "react"
import PropTypes from "prop-types"
import Select, { components } from "react-select"
import { Label } from "reactstrap"
import { MAX_CUSTOM_PAGE_DOCUMENT_IDS } from "helpers/customPageDocuments"

const getDocumentLabel = (documents, id) => {
  const doc = (documents || []).find(
    (row) => String(row?.id || "").toLowerCase() === String(id || "").toLowerCase()
  )
  return doc?.name || doc?.fileName || id
}

const optionFromDocument = (doc) => ({
  value: String(doc.id).toLowerCase(),
  label: doc.name || doc.fileName || String(doc.id),
})

const MultiValueRemove = (props) => {
  if (props.selectProps.isDisabled) return null
  return <components.MultiValueRemove {...props} />
}

const pickerStyles = {
  menuPortal: (base) => ({ ...base, zIndex: 999999, pointerEvents: "auto" }),
  menu: (base) => ({ ...base, zIndex: 999999, pointerEvents: "auto" }),
  option: (base) => ({ ...base, cursor: "pointer", pointerEvents: "auto" }),
  valueContainer: (base) => ({
    ...base,
    flexWrap: "wrap",
    gap: 6,
    paddingTop: 6,
    paddingBottom: 6,
  }),
  multiValue: (base) => ({
    ...base,
    maxWidth: "100%",
    margin: 0,
    backgroundColor: "#eef2f7",
    border: "1px solid #d1d7e0",
  }),
  multiValueLabel: (base) => ({
    ...base,
    whiteSpace: "normal",
    wordBreak: "break-word",
    color: "#495057",
    padding: "4px 6px",
  }),
  multiValueRemove: (base) => ({
    ...base,
    color: "#6c757d",
    ":hover": {
      backgroundColor: "#dee4ec",
      color: "#343a40",
    },
  }),
}

const CustomPageResourcePicker = ({
  documentIds,
  onChange,
  documents,
  documentsReady,
  documentsError = "",
  disabled = false,
}) => {
  const fieldId = useId()
  const library = Array.isArray(documents) ? documents : []
  const selectedIds = Array.isArray(documentIds) ? documentIds : []
  const emptyLibrary = documentsReady && library.length === 0
  const atCap = selectedIds.length >= MAX_CUSTOM_PAGE_DOCUMENT_IDS

  const options = useMemo(() => library.map(optionFromDocument), [library])
  const value = useMemo(
    () =>
      selectedIds.map((id) => {
        const key = String(id).toLowerCase()
        return (
          options.find((option) => option.value === key) || {
            value: key,
            label: getDocumentLabel(library, id),
          }
        )
      }),
    [selectedIds, options, library]
  )

  const handleChange = (next) => {
    onChange((next || []).map((option) => option.value).slice(0, MAX_CUSTOM_PAGE_DOCUMENT_IDS))
  }

  return (
    <div>
      <Label for={fieldId}>Linked resources</Label>

      {documentsError ? <div>{documentsError}</div> : null}

      {emptyLibrary ? <div>No documents assigned to this company.</div> : null}

      {!emptyLibrary || selectedIds.length > 0 ? (
        <Select
          inputId={fieldId}
          classNamePrefix="custom-page-resource-picker"
          isMulti
          options={options}
          value={value}
          onChange={handleChange}
          isDisabled={disabled}
          isOptionDisabled={() => atCap}
          hideSelectedOptions
          openMenuOnFocus
          openMenuOnClick
          closeMenuOnSelect={false}
          blurInputOnSelect={false}
          placeholder="Search company documents…"
          noOptionsMessage={({ inputValue }) =>
            inputValue ? "No matching documents." : "No documents available."
          }
          menuPortalTarget={typeof document !== "undefined" ? document.body : null}
          menuPosition="fixed"
          components={{ MultiValueRemove }}
          styles={pickerStyles}
        />
      ) : null}
    </div>
  )
}

CustomPageResourcePicker.propTypes = {
  documentIds: PropTypes.arrayOf(PropTypes.string).isRequired,
  onChange: PropTypes.func.isRequired,
  documents: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string,
      fileName: PropTypes.string,
    })
  ).isRequired,
  documentsReady: PropTypes.bool.isRequired,
  documentsError: PropTypes.string,
  disabled: PropTypes.bool,
}

export default CustomPageResourcePicker
